class StickyAddToCartContent extends HTMLElement {
  constructor() {
    super();

    // Sticky ATC (this component)
    this.stickyAddToCartButton = this.querySelector("[add-to-cart-button]");
    this.stickyButtonContainer = this.querySelector("[stikcy-add-to-cart-button-content]");

    this.stickyAddToCartButton?.addEventListener("click", () => {
      // Klaviyo Back in Stock: let Klaviyo handle the click (do not submit the product form)
      if (this.stickyAddToCartButton?.classList.contains("klaviyo-bis-trigger")) {
        return;
      }
      HELPER_UTIL.submitButtonState(this.stickyAddToCartButton).loadingState();
      HELPER_UTIL.dispatchCustomEvent(
        "submit",
        document.getElementById(this.stickyAddToCartButton.getAttribute("form"))
      );
    });

    // Breakpoints / responsive
    this.desktopBreakpointQuery = window.matchMedia("(min-width:768px)");
    // On resize: recalc scroll target + whether the shopper has scrolled past the main ATC
    this._boundOnResizeRecalculateScrollTarget = () => {
      this.recalculateScrollTargetOffset();
      this._syncMainAtcVisibilityFromRect();
      this.updateStickyVisibility();
    };
    this._boundOnClickScrollToSection = this._scrollToSection.bind(this);

    // Reset sticky button state (external event)
    document.addEventListener(_EVENT_HELPER.resetStickyAddTocartButton, () => {
      const btn = this.querySelector("[add-to-cart-button]");
      if (!btn) return;
      HELPER_UTIL.submitButtonState(btn).resetState(1000);
      HELPER_UTIL.submitButtonState(btn).disableState(false);
    });

    window.addEventListener("resize", this._boundOnResizeRecalculateScrollTarget);

    // If variant errors happen while sticky visible, scroll to variant section
    document.addEventListener(_EVENT_HELPER.triggerVariantAddToCartError, (e) => {
      const stickyHost = this.closest("sticky-add-to-cart");
      const isHidden = stickyHost?.getAttribute("aria-hidden");
      if (isHidden === "false") this._scrollToSection(e);
    });

    // Scroll-to-section wiring
    this.productContainer = document.querySelector("[product-container]");
    this.drawerScrollToSectionButton = document.querySelector("drawer-component [scroll-to-section]");
    this.stickyScrollToSectionButton = this.querySelector("[scroll-to-section]");

    this.stickyScrollToSectionButton?.addEventListener("click", this._boundOnClickScrollToSection);
    this.drawerScrollToSectionButton?.addEventListener("click", this._boundOnClickScrollToSection);

    this.scrollTargetSectionId =
      this.stickyScrollToSectionButton?.getAttribute("scroll-to-section") ||
      this.drawerScrollToSectionButton?.getAttribute("scroll-to-section");

    this.scrollTargetElement = this.scrollTargetSectionId
      ? document.querySelector(`#shopify-section-${this.scrollTargetSectionId} [variant-block]`)
      : null;

    this.stickyHostElement = this.closest("sticky-add-to-cart");
    // Sticky host + chat-button offset support
    this.updateChatButtonBottomOffset = () => {
      const stickyHost = this.stickyHostElement;
      if (!stickyHost) return;

      // Only push chat button up when sticky is actually visible
      const stickyIsActive = stickyHost.classList.contains("active");
      const stickyIsAriaHidden = stickyHost.getAttribute("aria-hidden") === "true";

      const stickyHeightPx =
        stickyIsActive && !stickyIsAriaHidden ? Math.ceil(stickyHost.getBoundingClientRect().height) : 0;

      document.documentElement.style.setProperty("--sticky-offset", `${stickyHeightPx}px`);
    };

    // Watch sticky height changes (content swaps, wrapping, etc.)
    this.stickyHostResizeObserver = new ResizeObserver(this.updateChatButtonBottomOffset);

    // Watch sticky visibility changes if class/aria-hidden is changed elsewhere
    this.stickyHostMutationObserver = new MutationObserver(this.updateChatButtonBottomOffset);

    // Footer visibility (hide sticky when footer visible)
    this.footerElement = document.querySelector("footer");
    this.isFooterVisibleInViewport = false;

    this.footerIntersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        this.isFooterVisibleInViewport = !!entry?.isIntersecting;
        this.updateStickyVisibility();
      },
      { root: null, threshold: 0.01 }
    );

    // Show sticky only after the shopper has scrolled *past* the main ATC (it sits above the viewport).
    // "Not intersecting" alone is wrong: the button can be below the fold on load (also not intersecting)
    // which incorrectly showed the sticky bar before they ever reached the real button.
    this.hasScrolledPastMainAtc = false;
    this.realAddToCartObservedElement = null;
    this.realAddToCartIntersectionObserver = null;
    this._observedAtcEl = null;
    this._scrollRaf = 0;

    this._boundAtcIntersection = (entries) => {
      const entry = entries[0];
      if (!entry) return;
      this._updateHasScrolledPastMainAtcFromRect(entry.boundingClientRect);
      this.updateStickyVisibility();
    };

    // IntersectionObserver can lag on some browsers; keep geometry in sync while scrolling
    this._boundOnScroll = () => {
      if (this._scrollRaf) return;
      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = 0;
        this._syncMainAtcVisibilityFromRect();
        this.updateStickyVisibility();
      });
    };
    window.addEventListener("scroll", this._boundOnScroll, { passive: true });

    // Variant updates can change layout or replace nodes; re-check main ATC + visibility
    this._boundOnVariantChanged = () => {
      requestAnimationFrame(() => {
        this._setupMainAtcObserver();
        this._syncMainAtcVisibilityFromRect();
        this.updateStickyVisibility();
      });
    };
    document.addEventListener("variant:changed", this._boundOnVariantChanged);
  }

  // Prefer the real PDP button inside [product-info], not the whole product form (too large for intersection)
  _getMainProductAtcButton() {
    const scoped =
      document.querySelector('[product-info] button[name="add"]') ||
      document.querySelector('[product-info] [data-real-atc]');
    if (scoped) return scoped;
    const marked = document.querySelector("[data-real-atc]");
    if (marked && marked.closest("[product-info]")) return marked;
    return document.querySelector('product-form.product-form button[name="add"]');
  }

  // True only when the main ATC is fully above the viewport (user scrolled down past it).
  _updateHasScrolledPastMainAtcFromRect(r) {
    if (!r || r.width <= 0 || r.height <= 0) {
      this.hasScrolledPastMainAtc = false;
      return;
    }
    this.hasScrolledPastMainAtc = r.bottom <= 0;
  }

  _syncMainAtcVisibilityFromRect() {
    const el = this.realAddToCartObservedElement;
    if (!el || !el.isConnected) {
      this.hasScrolledPastMainAtc = false;
      return;
    }
    this._updateHasScrolledPastMainAtcFromRect(el.getBoundingClientRect());
  }

  // Attach IO to the current main ATC node (handles DOM swaps / re-renders)
  _setupMainAtcObserver() {
    const next = this._getMainProductAtcButton();
    if (!next) {
      if (this._observedAtcEl && this.realAddToCartIntersectionObserver) {
        try {
          this.realAddToCartIntersectionObserver.unobserve(this._observedAtcEl);
        } catch (e) {}
      }
      this._observedAtcEl = null;
      this.realAddToCartObservedElement = null;
      this.hasScrolledPastMainAtc = false;
      return;
    }

    if (!this.realAddToCartIntersectionObserver) {
      this.realAddToCartIntersectionObserver = new IntersectionObserver(this._boundAtcIntersection, {
        root: null,
        threshold: 0,
        rootMargin: "0px",
      });
    }

    if (this._observedAtcEl && this._observedAtcEl !== next) {
      try {
        this.realAddToCartIntersectionObserver.unobserve(this._observedAtcEl);
      } catch (e) {}
    }

    this.realAddToCartObservedElement = next;
    this.realAddToCartIntersectionObserver.observe(next);
    this._observedAtcEl = next;
    this._syncMainAtcVisibilityFromRect();
  }

  connectedCallback() {
    // Icon markup fallback when switching icon layout back from text (sold out / notify)
    const tpl = this.querySelector("template[sticky-atc-icon-fallback]");
    if (tpl?.innerHTML) {
      this._iconButtonTextHTML = tpl.innerHTML.trim();
    }

    this.stickyAddToCartButton = this.querySelector("[add-to-cart-button]");

    // Observe sticky host for offset calculations
    if (this.stickyHostElement) {
      this.stickyHostResizeObserver.observe(this.stickyHostElement);
      this.stickyHostMutationObserver.observe(this.stickyHostElement, {
        attributes: true,
        attributeFilter: ["class", "aria-hidden"],
      });
    }

    // Observe footer
    if (this.footerElement) this.footerIntersectionObserver.observe(this.footerElement);

    // Observe real ATC (main product add button)
    this._setupMainAtcObserver();
    this.recalculateScrollTargetOffset();
    this.updateStickyVisibility();

    // Second pass after layout/fonts so the first visibility state matches what the user sees
    requestAnimationFrame(() => {
      this._setupMainAtcObserver();
      this._syncMainAtcVisibilityFromRect();
      this.updateStickyVisibility();
    });

    // Align sticky label/state with the primary product form (incl. Klaviyo BIS)
    const sectionId = document.querySelector("[product-info]")?.dataset?.sectionId;
    if (sectionId) this.syncFromMainProductForm(sectionId);
  }

  // Keep sticky buy button in sync with #product-form-{sectionId} button[name="add"]
  syncFromMainProductForm(sectionId) {
    if (!sectionId) return;
    const mainForm = document.getElementById(`product-form-${sectionId}`);
    if (!mainForm) return;
    const mainAdd = mainForm.querySelector('button[name="add"]');
    if (!mainAdd) return;

    const stickyBtn = this.querySelector("[add-to-cart-button]");
    if (!stickyBtn) return;

    if (typeof HELPER_UTIL.cleanupKlaviyoBisAdjacentToButton === "function") {
      HELPER_UTIL.cleanupKlaviyoBisAdjacentToButton(stickyBtn);
    }

    const mainBis = mainAdd.classList.contains("klaviyo-bis-trigger");
    const mainDisabled = mainAdd.disabled;
    const mainTextEl = mainAdd.querySelector(".btn--text");
    const mainPriceEl = mainAdd.querySelector(".buy-buttons-price");
    const layout = this.getAttribute("layout-type") || "";

    stickyBtn.classList.toggle("klaviyo-bis-trigger", mainBis);
    stickyBtn.disabled = mainDisabled;
    stickyBtn.classList.toggle("disable", mainDisabled);

    if (mainBis) {
      stickyBtn.removeAttribute("toggle-element");
    } else if (!mainDisabled) {
      stickyBtn.setAttribute("toggle-element", "cart-drawer");
    } else {
      stickyBtn.removeAttribute("toggle-element");
    }

    const stickyText = stickyBtn.querySelector(".btn--text");
    const stickyPrice = stickyBtn.querySelector(".buy-buttons-price");
    const placeholder = stickyBtn.querySelector(".placeholder-text");
    const mainLabel = (mainTextEl?.textContent || "").trim();

    if (layout === "bar") {
      if (placeholder) {
        const hidePlaceholder = mainBis || mainDisabled || !mainPriceEl;
        placeholder.hidden = hidePlaceholder;
        placeholder.style.display = hidePlaceholder ? "none" : "";
        if (!hidePlaceholder && mainPriceEl) {
          const phSpan = placeholder.querySelector("span");
          if (phSpan) phSpan.textContent = mainPriceEl.textContent.trim();
        }
      }
      if (stickyText) {
        if (mainPriceEl && stickyPrice && !mainBis && !mainDisabled) {
          stickyText.textContent = mainLabel ? `${mainLabel}` : mainLabel;
          stickyPrice.innerHTML = mainPriceEl.innerHTML;
        } else {
          stickyText.textContent = mainLabel;
          if (stickyPrice) stickyPrice.innerHTML = "";
        }
      }
    } else if (stickyText) {
      if (mainBis || mainDisabled) {
        if (stickyText.querySelector("svg") && !this._iconButtonTextHTML) {
          this._iconButtonTextHTML = stickyText.innerHTML;
        }
        stickyText.textContent = mainLabel;
        stickyText.classList.toggle("sticky-add-to-cart__label", true);
      } else {
        stickyText.classList.remove("sticky-add-to-cart__label");
        if (this._iconButtonTextHTML) {
          stickyText.innerHTML = this._iconButtonTextHTML;
        }
      }
    }

    // Main ATC geometry may change when copy/price updates
    requestAnimationFrame(() => {
      this._setupMainAtcObserver();
      this._syncMainAtcVisibilityFromRect();
      this.updateStickyVisibility();
    });
  }

  // Sticky visibility: show only after scrolling past the main ATC; hide when footer is in view
  updateStickyVisibility() {
    const stickyHost = this.stickyHostElement;
    if (!stickyHost) return;

    const shouldShowSticky =
      this.hasScrolledPastMainAtc && !this.isFooterVisibleInViewport;

    if (shouldShowSticky) stickyHost.classList.add("active");
    else stickyHost.classList.remove("active");

    this.updateChatButtonBottomOffset();
  }

  _scrollToSection(e) {
    e?.preventDefault?.();

    // NOTE: if drawerType isn't defined keep existing property wiring or remove this if unnecessary.
    HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.hideDrawer, this, {
      drawerType: this.drawerType,
    });

    if (this.scrollTargetOffsetTopPx > 0) {
      window.scrollTo({ top: this.scrollTargetOffsetTopPx, behavior: "smooth" });
    }
  }

  recalculateScrollTargetOffset() {
    if (!this.scrollTargetElement) {
      this.scrollTargetOffsetTopPx = 0;
      this.updateChatButtonBottomOffset();
      return;
    }

    if (this.desktopBreakpointQuery.matches === true) {
      this.scrollTargetOffsetTopPx =
        this.scrollTargetElement.offsetTop + (this.productContainer?.offsetTop || 0);
    } else {
      this.scrollTargetOffsetTopPx =
        (document.querySelector("[scroll-product-info]")?.offsetTop || 0) +
        this.scrollTargetElement.offsetTop;
    }

    this.updateChatButtonBottomOffset();
  }

  disconnectedCallback() {
    if (this._scrollRaf) {
      cancelAnimationFrame(this._scrollRaf);
      this._scrollRaf = 0;
    }
    window.removeEventListener("scroll", this._boundOnScroll);
    document.removeEventListener("variant:changed", this._boundOnVariantChanged);
    window.removeEventListener("resize", this._boundOnResizeRecalculateScrollTarget);

    this.stickyScrollToSectionButton?.removeEventListener("click", this._boundOnClickScrollToSection);
    this.drawerScrollToSectionButton?.removeEventListener("click", this._boundOnClickScrollToSection);

    if (this.stickyHostElement) {
      this.stickyHostResizeObserver.unobserve(this.stickyHostElement);
      this.stickyHostMutationObserver.disconnect();
    }
    this.stickyHostResizeObserver.disconnect();

    this.footerIntersectionObserver?.disconnect();
    if (this._observedAtcEl && this.realAddToCartIntersectionObserver) {
      try {
        this.realAddToCartIntersectionObserver.unobserve(this._observedAtcEl);
      } catch (e) {}
    }
    this.realAddToCartIntersectionObserver?.disconnect();
  }
}

customElements.get("sticky-add-to-cart-content") ||
  customElements.define("sticky-add-to-cart-content", StickyAddToCartContent);
